import { useEffect, useRef, useState } from 'react';
import { Button, Caption, Input, List, Placeholder, Title } from '@telegram-apps/telegram-ui';
import {
  BoxByCodeView,
  CreateQuestionResponse,
} from '@veylme/shared';

import { apiFetch, submitAppeal } from '../api/client.js';
import { ApiError } from '../api/errors.js';
import { AgeGateSheet } from '../components/AgeGateSheet.js';
import { QuestionField } from '../components/QuestionField.js';
import { useToast } from '../components/ToastProvider.js';
import { acknowledgeAge, isAgeAcknowledged } from '../lib/ageGate.js';
import { humanizeApiError } from '../lib/apiError.js';
import { isCompleteCode, normalizeCodeInput } from '../lib/codeInput.js';
import { clearDraft, readDraft, saveDraft } from '../lib/launchContext.js';
import { addSentQuestion } from '../lib/sentQuestions.js';
import { useT } from '../hooks/useT.js';
import { useBackButton, useMainButton } from '../lib/nativeUi.js';
import { hapticNotification } from '../lib/tg.js';
import styles from './AskByCodeScreen.module.css';

const QUESTION_MAX = 500;

/** Пауза перед записью черновика — пишем не на каждую букву (work-038 §5.3). */
const DRAFT_DEBOUNCE_MS = 500;

/**
 * AskByCodeScreen — второй вход зрителя: ввод короткого кода вместо deep-link
 * (work-020, решение 3). Не-Premium авторы не получают кликабельную ссылку в
 * сторис → показывают код, зритель вводит его здесь и задаёт анонимный вопрос.
 *
 * Две фазы в ОДНОМ компоненте, ОДИН `useMainButton` (конфиг по фазе+состоянию —
 * НЕ два владельца нативной кнопки):
 *  - enter:   поле кода → GET /boxes/by-code/:code. 404/inactive → инлайн «код не
 *             найден» (анти-оракул на сервере: промах/неактив/невалид = один 404),
 *             НЕ падаем. Success+isActive → phase 'compose'.
 *  - compose: форма вопроса (QuestionField + AgeGateSheet) → POST /questions/by-code
 *             {code,text} — БЕЗ token (резолв по коду, CANON B2). Словарь ошибок /
 *             success / haptic — как в AskScreen.
 *
 * BackButton: compose → enter; enter → onClose (выход в INBOX).
 * AskScreen и AskByCodeScreen НИКОГДА не смонтированы вместе (App — state-machine)
 * → у каждого свой единственный useMainButton, конфликта нет.
 *
 * work-038: набранный текст переживает холодный перезапуск приложения. Код в
 * фазе 'enter' ещё не известен серверу, поэтому черновик читается/пишется по
 * `code` ТОЛЬКО в фазе 'compose' — как только код резолвится (`findByCode`),
 * подставляем сохранённый текст ЭТОГО кода; дебаунс — как в `lib/launchContext.ts`.
 */
export interface AskByCodeScreenProps {
  /** Назад из фазы enter — выход с экрана (App → INBOX). */
  onClose: () => void;
  /** После успешной отправки CTA «Создай свой ящик» (App → INBOX). */
  onCreateOwnBox: () => void;
}

type Phase =
  | { kind: 'enter' }
  | { kind: 'compose'; code: string; box: BoxByCodeView };

type EnterState =
  | { kind: 'idle' }
  | { kind: 'finding' }
  | { kind: 'error'; message: string };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }
  // work-024 C: sender_banned — показываем кнопку «Обжаловать»
  | { kind: 'banned'; message: string; appealState: 'idle' | 'sending' | 'sent' };

export function AskByCodeScreen({ onClose, onCreateOwnBox }: AskByCodeScreenProps) {
  const t = useT();
  const { showError } = useToast();
  const [phase, setPhase] = useState<Phase>({ kind: 'enter' });
  const [code, setCode] = useState('');
  const [enterState, setEnterState] = useState<EnterState>({ kind: 'idle' });
  // work-038: черновик читается по `code` только в фазе 'compose' (см. докстринг
  // файла) — на mount, до резолва кода, подставлять нечего.
  const [text, setText] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  // CANON I2: age-gate перед первой отправкой (общий ключ с AskScreen).
  const [ageGateOpen, setAgeGateOpen] = useState(false);
  // work-038: таймер дебаунса записи черновика — держим в ref, а не в state
  // (переустановка таймера не должна вызывать лишний ререндер).
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // work-038: «черновик потреблён» — выставляется в момент clearDraft() ниже и
  // гасит отложенную запись, если таймер всё же успел встать в очередь до
  // clearTimeout в sendQuestion (защита от гонки, см. докстринг файла).
  const draftConsumedRef = useRef(false);

  // work-038: адресат черновика известен только в фазе 'compose'.
  const composeCode = phase.kind === 'compose' ? phase.code : undefined;

  // work-038: дебаунс записи черновика — пишем не на каждую букву, а спустя
  // DRAFT_DEBOUNCE_MS после последней правки, и только когда код уже известен.
  // Cleanup гасит таймер и при каждой новой правке, и при размонтировании —
  // протечки в следующий тест быть не должно.
  useEffect(() => {
    if (composeCode === undefined) return undefined;
    draftTimerRef.current = setTimeout(() => {
      if (draftConsumedRef.current) return;
      saveDraft({ screen: 'ASK_BY_CODE', code: composeCode, text });
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(draftTimerRef.current);
  }, [composeCode, text]);

  // — phase enter: резолв кода —
  async function findByCode() {
    if (!isCompleteCode(code)) {
      setEnterState({ kind: 'error', message: t.ask_code_invalid });
      return;
    }
    setEnterState({ kind: 'finding' });
    try {
      const box = await apiFetch(`/boxes/by-code/${encodeURIComponent(code)}`, {
        parse: BoxByCodeView,
      });
      if (!box.isActive) {
        // Контракт: by-code никогда не отдаёт isActive=false (404 анти-оракул),
        // но на всякий случай — инлайн «не найден», не падаем.
        setEnterState({ kind: 'error', message: t.ask_code_not_found });
        return;
      }
      setEnterState({ kind: 'idle' });
      setPhase({ kind: 'compose', code, box });
      // work-038: код только что резолвился — подставляем черновик ЭТОГО кода
      // (полное совпадение адресата, см. lib/launchContext.ts).
      draftConsumedRef.current = false;
      setText(readDraft({ screen: 'ASK_BY_CODE', code }) ?? '');
    } catch (err: unknown) {
      // 404 = промах/неактив/невалид (анти-оракул) → инлайн «код не найден», НЕ падаем.
      if (err instanceof ApiError && err.status === 404) {
        setEnterState({ kind: 'error', message: t.ask_code_not_found });
        return;
      }
      setEnterState({ kind: 'error', message: humanizeApiError(err, t) });
    }
  }

  function handleCodeChange(raw: string) {
    setCode(normalizeCodeInput(raw));
    // Сбрасываем инлайн-ошибку, как только пользователь правит ввод.
    if (enterState.kind === 'error') setEnterState({ kind: 'idle' });
  }

  // — phase compose: отправка вопроса по коду —
  function handleSubmit() {
    if (text.trim().length === 0) return;
    if (!isAgeAcknowledged()) {
      setAgeGateOpen(true);
      return;
    }
    void sendQuestion();
  }

  function handleAgeConfirm() {
    acknowledgeAge();
    setAgeGateOpen(false);
    void sendQuestion();
  }

  async function sendQuestion() {
    if (phase.kind !== 'compose') return;
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    // work-038 (ревью нашло дефект 1): flush-then-send. Сначала гасим
    // отложенную запись, ЗАТЕМ синхронно пишем черновик на диск и только
    // потом шлём запрос. Раньше здесь стоял голый clearTimeout — если
    // отправка проваливалась (в первую очередь ради 401 всё и затевалось),
    // черновик нигде не оставался: catch-ветка saveDraft не зовёт, а эффект
    // дебаунса не перезапустится (его deps [composeCode, text] не поменялись),
    // и при 401 глобальный перехват мгновенно подменяет экран на
    // SessionExpiredScreen — композер размонтируется, набранный текст исчезал
    // бесследно ровно в момент, ради которого весь work-038 и затевался.
    // Запись ДО сетевого вызова это чинит и НЕ возвращает старый дефект
    // «отправленный текст воскресает»: отложенного таймера уже нет (clearTimeout
    // выше), а при успехе ниже clearDraft стирает запись — инвариант «после
    // успешной отправки в хранилище черновика нет» держится.
    clearTimeout(draftTimerRef.current);
    saveDraft({ screen: 'ASK_BY_CODE', code: phase.code, text });
    setSubmitState({ kind: 'submitting' });
    try {
      // CANON B2: резолв по коду — тело { code, text }, БЕЗ token.
      const created = await apiFetch('/questions/by-code', {
        method: 'POST',
        body: { code: phase.code, text: trimmed },
        parse: CreateQuestionResponse,
      });
      // retention-answer-loop: запоминаем questionId на устройстве спрашивающего
      // (localStorage, БЕЗ серверной identity) — «Мои вопросы» подтянет ответ
      // по этому id, когда автор ответит.
      addSentQuestion({
        questionId: created.questionId,
        createdAt: new Date().toISOString(),
        label: phase.box.prompt ?? undefined,
      });
      // work-038: черновик потреблён — стираем и гасим отложенную запись.
      draftConsumedRef.current = true;
      clearDraft({ screen: 'ASK_BY_CODE', code: phase.code });
      hapticNotification('success');
      setSubmitState({ kind: 'success' });
    } catch (err: unknown) {
      const message = humanizeApiError(err, t);
      hapticNotification('error');
      // work-024 C: sender_banned → специальное состояние с кнопкой «Обжаловать»
      if (err instanceof ApiError && err.code === 'sender_banned') {
        setSubmitState({ kind: 'banned', message, appealState: 'idle' });
      } else {
        showError(message);
        setSubmitState({ kind: 'error', message });
      }
    }
  }

  // work-024 C: отправка апелляции при бане (фаза compose)
  async function handleAppeal() {
    if (submitState.kind !== 'banned') return;
    setSubmitState({ ...submitState, appealState: 'sending' });
    try {
      await submitAppeal();
      setSubmitState({ ...submitState, appealState: 'sent' });
    } catch {
      // fail-soft: не ломаем UI, сброс в idle чтобы можно было повторить
      setSubmitState({ ...submitState, appealState: 'idle' });
    }
  }

  // — BackButton: compose → enter, enter → onClose —
  useBackButton(() => {
    if (phase.kind === 'compose' && submitState.kind !== 'success') {
      setPhase({ kind: 'enter' });
      setSubmitState({ kind: 'idle' });
      return;
    }
    onClose();
  });

  const submitting = submitState.kind === 'submitting';
  const canSubmit = !submitting && text.trim().length > 0;
  const isComposeForm =
    phase.kind === 'compose' &&
    submitState.kind !== 'success' &&
    submitState.kind !== 'banned';
  const showCreateOwn = submitState.kind === 'success';

  // Единый MainButton на весь экран (конфиг по фазе+состоянию):
  //  - enter:   «Найти» (enabled при полном коде; loading при резолве);
  //  - compose: «Отправить»/«Создай свой ящик» (как AskScreen).
  // Под открытой age-шторкой кнопку прячем — там свои действия.
  useMainButton(
    ageGateOpen
      ? { text: '', onClick: () => undefined, visible: false }
      : phase.kind === 'enter'
        ? {
            text: t.ask_code_find,
            onClick: () => void findByCode(),
            enabled: isCompleteCode(code) && enterState.kind !== 'finding',
            loading: enterState.kind === 'finding',
          }
        : isComposeForm
          ? {
              text: submitting ? t.ask_sending : t.ask_send,
              onClick: handleSubmit,
              enabled: canSubmit,
              loading: submitting,
            }
          : showCreateOwn
            ? { text: t.ask_create_own_box, onClick: onCreateOwnBox }
            : { text: '', onClick: () => undefined, visible: false },
  );

  function renderBody() {
    if (phase.kind === 'enter') {
      return (
        <>
          <div className={styles.head}>
            <Title level="2" weight="2">
              {t.ask_code_title}
            </Title>
            <Caption level="1" className={styles.hint}>
              {t.ask_code_desc}
            </Caption>
          </div>
          <div
            className={
              enterState.kind === 'error'
                ? `${styles.codeField} ${styles.codeFieldError}`
                : styles.codeField
            }
          >
            <Input
              className={styles.codeInput}
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder={t.ask_code_placeholder}
              inputMode="numeric"
              autoComplete="one-time-code"
              status={enterState.kind === 'error' ? 'error' : 'default'}
            />
          </div>
          {enterState.kind === 'error' && (
            <Caption
              Component="p"
              role="alert"
              className={styles.codeError}
            >
              {enterState.message}
            </Caption>
          )}
        </>
      );
    }

    // phase compose
    if (submitState.kind === 'success') {
      return (
        <Placeholder
          header={t.ask_sent_title}
          description={
            <>
              {t.ask_sent_desc}
              <br />
              <Caption level="1" className={styles.hint}>
                {t.ask_sent_find_reply_hint}
              </Caption>
            </>
          }
        />
      );
    }
    // work-024 C: бан — показываем ошибку + кнопку «Обжаловать»
    if (submitState.kind === 'banned') {
      return (
        <Placeholder
          header={t.error_title}
          description={
            submitState.appealState === 'sent'
              ? t.ask_appeal_sent
              : submitState.message
          }
          action={
            submitState.appealState !== 'sent' ? (
              <Button
                size="m"
                onClick={() => void handleAppeal()}
                disabled={submitState.appealState === 'sending'}
              >
                {t.ask_banned_appeal_btn}
              </Button>
            ) : undefined
          }
        />
      );
    }

    const promptText = phase.box.prompt ?? t.tagline;
    return (
      <>
        <div className={styles.head}>
          <Title level="2" weight="2">
            {promptText}
          </Title>
          <Caption level="1" className={styles.hint}>
            {t.ask_anonymous_hint}
          </Caption>
        </div>
        <QuestionField
          value={text}
          onChange={setText}
          maxLength={QUESTION_MAX}
          placeholder={t.ask_placeholder}
          disabled={submitting}
          error={
            submitState.kind === 'error' ? submitState.message : undefined
          }
        />
      </>
    );
  }

  return (
    <List>
      {renderBody()}

      <AgeGateSheet
        open={ageGateOpen}
        onConfirm={handleAgeConfirm}
        onCancel={() => setAgeGateOpen(false)}
      />
    </List>
  );
}
